import { Injectable, ErrorHandler, inject } from '@angular/core';
import { Router } from '@angular/router';
import { isProductionHost } from '../../shared/utils/environment';

interface ErrorLike {
  status?: number;
  name?: string;
  message?: string;
  stack?: string;
}

interface ErrorLogEntry {
  timestamp: string;
  type: string;
  message: string;
  error: { name?: string; message?: string; stack?: string; status?: number };
  userAgent: string;
  url: string;
}

function asErrorLike(error: unknown): ErrorLike {
  if (typeof error === 'object' && error !== null) {
    return error as ErrorLike;
  }
  return {};
}

/**
 * Global error handler service for the application
 * Provides centralized error handling, logging, and user feedback
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
  private router = inject(Router);
  private errorQueue: Array<{ error: unknown; timestamp: Date }> = [];
  private readonly MAX_ERROR_QUEUE_SIZE = 50;

  /**
   * Handle uncaught errors globally
   * @param error - The error to handle (typed as unknown for safe narrowing)
   */
  handleError(error: unknown): void {
    // Add to error queue for monitoring
    this.addToErrorQueue(error);

    // Log to console in development
    if (!this.isProduction()) {
      console.error('Global error caught:', error);
      const e = asErrorLike(error);
      if (e.stack) {
        console.error('Stack trace:', e.stack);
      }
    }

    // Extract meaningful error message
    const errorMessage = this.extractErrorMessage(error);
    const errorType = this.categorizeError(error);

    // Log error details
    this.logError(errorType, errorMessage, error);

    // Handle specific error types
    switch (errorType) {
      case 'network':
        this.handleNetworkError();
        break;
      case 'authentication':
        this.handleAuthenticationError();
        break;
      case 'authorization':
        this.handleAuthorizationError();
        break;
      case 'validation':
        this.handleValidationError(error);
        break;
      case 'notFound':
        this.handleNotFoundError(error);
        break;
      default:
        this.handleGenericError(errorMessage);
    }

    // In production, send error to monitoring service
    if (this.isProduction()) {
      this.sendToMonitoring(errorType, errorMessage, error);
    }
  }

  /**
   * Extract a meaningful error message from various error types
   */
  private extractErrorMessage(error: unknown): string {
    const e = asErrorLike(error);

    // HTTP errors
    if (e.status) {
      switch (e.status) {
        case 400:
          return 'Invalid request. Please check your input and try again.';
        case 401:
          return 'You are not authenticated. Please log in.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 409:
          return 'This operation conflicts with existing data.';
        case 500:
          return 'A server error occurred. Please try again later.';
        case 503:
          return 'The service is temporarily unavailable. Please try again later.';
        default:
          return `An error occurred (${e.status}). Please try again.`;
      }
    }

    // Network errors
    if (e.name === 'NetworkError' || e.message?.includes('network')) {
      return 'Network connection error. Please check your internet connection.';
    }

    // TypeScript/JavaScript errors
    if (error instanceof TypeError) {
      return 'A programming error occurred. Please refresh the page.';
    }
    if (error instanceof ReferenceError) {
      return 'A programming error occurred. Please refresh the page.';
    }

    // Default message
    return typeof error === 'string' ? error : 'An unexpected error occurred. Please try again.';
  }

  /**
   * Categorize error type for appropriate handling
   */
  private categorizeError(error: unknown): string {
    const e = asErrorLike(error);

    // Network errors
    if (e.name === 'NetworkError' ||
        e.message?.includes('network') ||
        e.message?.includes('Failed to fetch')) {
      return 'network';
    }

    // HTTP status-based categorization
    if (e.status) {
      if (e.status === 401) return 'authentication';
      if (e.status === 403) return 'authorization';
      if (e.status === 404) return 'notFound';
      if (e.status === 400 || e.status === 422) return 'validation';
    }

    return 'generic';
  }

  /**
   * Handle network errors
   */
  private handleNetworkError(): void {
    this.showUserMessage(
      'Network Error',
      'Unable to connect to the server. Please check your internet connection and try again.',
      'error'
    );
  }

  /**
   * Handle authentication errors
   */
  private handleAuthenticationError(): void {
    this.showUserMessage(
      'Authentication Required',
      'Your session has expired. Please log in again.',
      'warning'
    );
    // Redirect to login page after a delay
    setTimeout(() => {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url }
      });
    }, 2000);
  }

  /**
   * Handle authorization errors
   */
  private handleAuthorizationError(): void {
    this.showUserMessage(
      'Access Denied',
      'You do not have permission to perform this action.',
      'error'
    );
  }

  /**
   * Handle validation errors
   */
  private handleValidationError(error: unknown): void {
    const message = this.extractErrorMessage(error);
    this.showUserMessage('Validation Error', message, 'warning');
  }

  /**
   * Handle not found errors
   */
  private handleNotFoundError(error: unknown): void {
    const message = this.extractErrorMessage(error);
    this.showUserMessage('Not Found', message, 'warning');
  }

  /**
   * Handle generic errors
   */
  private handleGenericError(message: string): void {
    this.showUserMessage('Error', message, 'error');
  }

  /**
   * Show user-friendly message (uses browser alert for now, can be replaced with toast/snackbar)
   */
  private showUserMessage(title: string, message: string, type: 'error' | 'warning' | 'info'): void {
    // For now, use console logging and alert
    // In a real application, this would integrate with a toast/notification service
    console.log(`[${type.toUpperCase()}] ${title}: ${message}`);

    // Only show alerts for critical errors
    if (type === 'error') {
      // In production, you'd use a proper toast notification library
      // For now, we'll just log to console to avoid annoying alerts
      console.error(`${title}: ${message}`);
    }
  }

  /**
   * Log error details
   */
  private logError(type: string, message: string, error: unknown): void {
    const e = asErrorLike(error);
    const logEntry: ErrorLogEntry = {
      timestamp: new Date().toISOString(),
      type,
      message,
      error: {
        name: e.name,
        message: e.message,
        stack: e.stack,
        status: e.status
      },
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Log to console
    console.error('[ERROR LOG]', logEntry);

    // In production, you would send this to a logging service
    // For now, store in localStorage for debugging
    if (!this.isProduction()) {
      this.storeErrorLog(logEntry);
    }
  }

  /**
   * Store error log in localStorage for debugging
   */
  private storeErrorLog(logEntry: ErrorLogEntry): void {
    try {
      const logs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      logs.push(logEntry);
      // Keep only last 100 logs
      const trimmedLogs = logs.slice(-100);
      localStorage.setItem('errorLogs', JSON.stringify(trimmedLogs));
    } catch (e) {
      console.error('Failed to store error log:', e);
    }
  }

  /**
   * Add error to queue for monitoring
   */
  private addToErrorQueue(error: unknown): void {
    this.errorQueue.push({
      error,
      timestamp: new Date()
    });

    // Trim queue if it gets too large
    if (this.errorQueue.length > this.MAX_ERROR_QUEUE_SIZE) {
      this.errorQueue = this.errorQueue.slice(-this.MAX_ERROR_QUEUE_SIZE);
    }
  }

  /**
   * Send error to monitoring service (placeholder)
   */
  private sendToMonitoring(type: string, message: string, error: unknown): void {
    // Placeholder for integration with monitoring services like:
    // - Sentry
    // - LogRocket
    // - Application Insights
    // - Custom logging endpoint

    console.log('[MONITORING] Would send error to monitoring service:', {
      type,
      message,
      error
    });
  }

  private isProduction(): boolean {
    return isProductionHost();
  }

  /**
   * Get recent errors (for debugging/monitoring dashboard)
   */
  getRecentErrors(): Array<{ error: unknown; timestamp: Date }> {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrorQueue(): void {
    this.errorQueue = [];
  }

  /**
   * Get error statistics
   */
  getErrorStatistics(): { total: number; byType: Record<string, number>; lastError: Date | null } {
    const byType: Record<string, number> = {};

    this.errorQueue.forEach(({ error }) => {
      const type = this.categorizeError(error);
      byType[type] = (byType[type] || 0) + 1;
    });

    return {
      total: this.errorQueue.length,
      byType,
      lastError: this.errorQueue.length > 0
        ? this.errorQueue[this.errorQueue.length - 1].timestamp
        : null
    };
  }
}

/**
 * Helper service for programmatic error handling in components and services
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  /**
   * Handle service errors with user feedback
   */
  handleServiceError(error: unknown, context?: string): never {
    const message = this.extractUserFriendlyMessage(error);
    const fullMessage = context ? `${context}: ${message}` : message;

    console.error('[Service Error]', { context, error, message });

    throw new Error(fullMessage);
  }

  /**
   * Extract user-friendly message from error
   */
  private extractUserFriendlyMessage(error: unknown): string {
    const e = asErrorLike(error);
    if (e.status) {
      switch (e.status) {
        case 400: return 'Invalid request';
        case 401: return 'Authentication required';
        case 403: return 'Access denied';
        case 404: return 'Resource not found';
        case 409: return 'Conflict with existing data';
        case 500: return 'Server error';
        default: return 'An error occurred';
      }
    }

    return 'An unexpected error occurred';
  }

  /**
   * Wrap async operations with error handling
   */
  async wrapAsync<T>(
    operation: () => Promise<T>,
    errorContext?: string
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      return this.handleServiceError(error, errorContext);
    }
  }

  /**
   * Validate and throw if invalid
   */
  validate(condition: boolean, message: string): void {
    if (!condition) {
      throw new Error(message);
    }
  }

  /**
   * Create user-friendly error
   */
  createError(message: string, originalError?: unknown): Error {
    const error = new Error(message);
    if (originalError !== undefined) {
      (error as Error & { originalError: unknown }).originalError = originalError;
    }
    return error;
  }
}
